import axios from 'axios';
import { isEqual, sortBy, isArray } from 'lodash';
import EnvironmentUtils from '../../shared/utils/environment-utils';
import { mapAxiosResponse } from '../../shared/utils/api-utils';
import mapResponseOnUserInfo, {
  UserPayload,
  UserAvatar,
  UserInfo,
  UserInfoResponse,
  UserRoles,
  UserRights,
  UserPasswordPayload, AgentUpdatePayload,
} from '../../shared/models/user';
import AuthUtils from '../../shared/utils/auth-utils';
import EnrouteApiClient from '../../shared/services/enroute-api-client';

const apiEnrouteUrl = EnvironmentUtils.getVar('API_ENROUTE');

export default class UserService {
  static async getUserInfo(id?: number): Promise<UserInfo> {
    const url = `/agent/${id || 'self'}?avatar=true`;

    const resp = await EnrouteApiClient.get(url);
    return mapResponseOnUserInfo(mapAxiosResponse<UserInfoResponse>(resp));
  }

  static async updateUserAvatar(avatar: UserAvatar, id?: number) {
    const headers = {
      Authorization: `Bearer ${AuthUtils.getAccessToken()}`,
      'Content-Type': avatar.type || 'image/jpeg',
    };

    const resp = await EnrouteApiClient.post(`/agent/${id || 'self'}/update_avatar`, avatar.file, { headers });
    return mapResponseOnUserInfo(mapAxiosResponse<UserInfoResponse>(resp));
  }

  static async updateUserPassword(userId: number, payload: UserPasswordPayload) {
    const resp = await EnrouteApiClient.post(`/agent/${userId || 'self'}/link_account`, {
      type: 'tracking',
      profile: {
        password: payload.password,
      },
    });
    return mapResponseOnUserInfo(mapAxiosResponse<UserInfoResponse>(resp));
  }

  static async updateUserName(name: string, id?: string) {
    const url = `${apiEnrouteUrl}agent/${id || 'self'}/profile_update`;

    const headers = {
      Authorization: `Bearer ${AuthUtils.getAccessToken()}`,
    };

    const resp = await axios.post(url, {
      name
    }, { headers });
    return mapResponseOnUserInfo(mapAxiosResponse<UserInfoResponse>(resp));
  }

  static async removeUser(id: string) {
    const resp = await EnrouteApiClient.post(`/agent/${id}/remove`);

    return mapAxiosResponse(resp as any);
  }

  static async createUser(orgId, payload: UserPayload & UserPasswordPayload, children?: boolean) {
    const userResponse = await EnrouteApiClient.post(`/org/${orgId}/create_agent`, {
      type: 'tracking',
      name: payload.name,
      display_name: payload.name,
      email: payload.email,
      password: payload.password,
      roles: payload.roles,
      info: {
        phones: [
          {
            type: 'cell',
            number: payload.phone,
          },
        ],
      },
    }, {
      params: {
        notify: true,
      },
    });

    const user = mapAxiosResponse<UserInfoResponse>(userResponse)

    await this.updateUserRoles(user._id, user.org_id, null, children);

    if (payload.avatar && payload.avatar.file) {
      await UserService.updateUserAvatar(payload.avatar, user._id);
    }

    return this.getUserInfo(user._id);
  }

  static async updateProfileUser(userId: number, payload: UserPayload) {
    const userResponse = await EnrouteApiClient.post(`/agent/${userId}/profile_update`, {
      name: payload.name,
      display_name: payload.name,
      email: payload.email,
      tags: payload.tags,
      info: {
        phones: [
          {
            type: 'cell',
            number: payload.phone,
          },
        ],
      },
    });

    const user = mapAxiosResponse<UserInfoResponse>(userResponse);

    const rolesChanged = !isEqual(sortBy(payload.roles), sortBy(user.roles))
    if (isArray(payload.roles) && rolesChanged) {
      await this.updateUserRoles(userId, user.org_id, payload.roles);
    }

    const orgIdChanged = payload?.organization !== user?.org_id;
    if (orgIdChanged && payload?.organization) {
      await this.migrateUser(userId, payload.organization);
    }

    if (payload.avatar && payload.avatar.file) {
      await UserService.updateUserAvatar(payload.avatar, userId);
    }

    return this.getUserInfo(userId);
  }

  static async updateUserRoles(userId: number, orgId: number,  roles: UserRoles[], children?: boolean): Promise<UserRights[]> {
    const rightsResponse = await EnrouteApiClient.get(`/agent/${userId}/rights`);

    const userRightsList = mapAxiosResponse<UserRights[]>(rightsResponse);

    const userRights = userRightsList.find((rights) => rights.org_id === orgId);

    const changeRoleResponse = await EnrouteApiClient.post(`/right/${userRights._id}/update`, {
      roles: roles ?? userRights.roles,
      children: children ?? userRights.children
    });

    return mapAxiosResponse<UserRights[]>(changeRoleResponse);
  }

  static async updateAgent(userId: number, payload: AgentUpdatePayload) {
    const userResponse = await EnrouteApiClient.post(`/agent/${userId}/update`, {
      ...payload
    });

    return mapResponseOnUserInfo(mapAxiosResponse<UserInfoResponse>(userResponse));
  }

  static async reInviteUser(userId: number) {
    await this.updateUserPassword(userId, {
      password: process.env.USER_DEFAULT_PASSWORD,
      passwordConfirm: process.env.USER_DEFAULT_PASSWORD,
    });

    const response = await EnrouteApiClient.post(`/agent/${ userId }/reinvite`);

    return mapAxiosResponse(response);
  }

  static async migrateUser(userId: number, orgId: number) {
    const response = await EnrouteApiClient.post(`/agent/${ userId }/migrate?org_id=${orgId}&keep_rights=true`);

    return mapAxiosResponse(response);
  }
}
